import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlanSelectScreen from '../../screens/PlanSelectScreen.jsx';
import TrialCardCaptureContainer from '../../screens/TrialCardCaptureContainer.jsx';
import { usePlanCatalog } from '../../hooks/usePlanCatalog.js';
import { hostedTrialCheckoutEnabled } from '../../lib/flags.js';
import { resolveManagedPaymentsNewSubscriptions } from '../../lib/managedPayments.js';
import { wallReturnTo, writeResumeIntent } from '../../lib/freePreview.js';
import { trackPreview } from '../../lib/freePreviewEvents.js';

function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}
export default function TrialWallOverlay({ wall, entitlements, onClose, onPaid }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { managedPaymentsNewSubscriptions: catalogManagedPayments } = usePlanCatalog();
  const fromPlansRoute = Boolean(wall?.meta?.fromPlansRoute);
  const returnTo = wallReturnTo({ pathname: location.pathname, search: location.search, fromPlansRoute });
  const pushedRef = useRef(false);
  const closingHowRef = useRef(null);
  const closedRef = useRef(false);
  const finish = useCallback((how) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose?.(how);
    if (fromPlansRoute) navigate('/app', { replace: true });
  }, [onClose, fromPlansRoute, navigate]);
  const requestClose = useCallback((how) => {
    if (closedRef.current) return;
    let ownEntry = false;
    try { ownEntry = pushedRef.current && window.history.state?.fpWall === true; } catch { ownEntry = false; }
    if (ownEntry) {
      closingHowRef.current = how;
      try { window.history.back(); return; } catch { /* fall through */ }
    }
    finish(how);
  }, [finish]);
  useEffect(() => {
    try {
      window.history.pushState({ ...(window.history.state || {}), fpWall: true }, '', window.location.href);
      pushedRef.current = true;
    } catch { pushedRef.current = false; }
    const onPop = () => { finish(closingHowRef.current || 'browser_back'); };
    const onKey = (event) => { if (event.key === 'Escape') requestClose('esc'); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const beforeCheckout = (planKey) => {
    writeResumeIntent(sessionStore(), { source: wall.source, postId: wall.meta?.postId || null, plan: planKey || null });
    trackPreview('preview_checkout_started', { source: wall.source, plan: planKey });
  };
  const managedPaymentsNewSubscriptions = resolveManagedPaymentsNewSubscriptions({ catalogFlag: catalogManagedPayments, entitlements });
  const hosted = hostedTrialCheckoutEnabled({ managedPaymentsNewSubscriptions });
  const previewContext = {
    source: wall.source, thumbUrl: wall.meta?.thumbUrl || null, thumbTitle: wall.meta?.thumbTitle || null,
    featureLabel: wall.meta?.featureLabelKey ? t(wall.meta.featureLabelKey) : '',
  };
  return (
    <div className="fpw-overlay" role="dialog" aria-modal="true" aria-label={t('freePreview.nextTitle')}>
      {hosted ? <PlanSelectScreen onComplete={onPaid} previewContext={previewContext} onBack={() => requestClose('back')} returnTo={returnTo} onBeforeCheckout={beforeCheckout} /> : (
        <div className="fpw-killswitch">
          <button type="button" className="fpw-back fpw-back--inline" onClick={() => requestClose('back')}><span aria-hidden="true">←</span> {t('freePreview.wallBack')}</button>
          <TrialCardCaptureContainer onComplete={(activation) => { beforeCheckout(activation?.planKey || null); return onPaid?.(activation); }} />
        </div>
      )}
    </div>
  );
}
